import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, Alert, TablePagination, TextField,
  InputAdornment, IconButton, Button, Breadcrumbs, Chip
} from '@mui/material';
import { Refresh, AccountBalanceWallet, Download, Upload } from '@mui/icons-material';
import { getVendorPayoutList, exportPayoutExcel, importPayoutExcel } from '../../services/Api';


const GetAllPayoutList = () => {
    const [payouts, setPayouts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Pagination & Filters
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [minAmount, setMinAmount] = useState('3000');
    const [debouncedMinAmount, setDebouncedMinAmount] = useState('3000');
    
    // Excel handlers
    const [downloading, setDownloading] = useState(false);
    const [uploading, setUploading] = useState(false);

    const handleDownload = async () => {
        try {
            setDownloading(true);
            const response = await exportPayoutExcel();
            // Create blob link to download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a'); // changed link to a
            link.href = url;
            link.setAttribute('download', 'Payouts_List.xlsx');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || err.message || 'Failed to download excel file');
        } finally {
            setDownloading(false);
        }
    };

    const handleUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            setUploading(true);
            setError(null);
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await importPayoutExcel(formData);
            if (response.success || response.message) {
                fetchPayouts(); // refresh table
                setSuccess(response.message || 'Excel uploaded successfully. Payout statuses updated.');
                setTimeout(() => setSuccess(null), 5000); // auto dismiss
            } else {
                setError(response?.message || 'Failed to upload excel file');
            }
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || 'Error occurred during upload');
        } finally {
            setUploading(false);
            event.target.value = null; // clear the input
        }
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedMinAmount(minAmount);
            setPage(0);
        }, 800);
        return () => clearTimeout(handler);
    }, [minAmount]);

    const fetchPayouts = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getVendorPayoutList(page + 1, rowsPerPage, debouncedMinAmount);
            if (response.success || response.data) {
                const dataArr = response.data?.docs || response.data?.payouts || response.data?.data || response.data || [];
                setPayouts(Array.isArray(dataArr) ? dataArr : []);
                setTotalRows(response?.totalRecords || response?.totalItems || response?.totalCount || response?.totalDocs || response?.total || response?.data?.totalRecords || response?.data?.totalItems || response?.data?.totalCount || response?.data?.totalDocs || response?.data?.total || response?.data?.totalElements || response?.data?.length || 0);
            } else {
                setError(response?.message || 'Failed to fetch payout list');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred while fetching payouts');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPayouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, debouncedMinAmount]);
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box>
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                    <Typography color="text.primary">Vendor Wallet</Typography>
                    <Typography color="text.primary">Payout Lists</Typography>
                </Breadcrumbs>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AccountBalanceWallet sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 300 }}>
                                Eligible Payouts
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                View vendors eligible for payouts based on minimum balance thresholds
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton 
                        onClick={fetchPayouts} 
                        disabled={loading} 
                        sx={{ color: 'primary.main', border: '1px solid', borderColor: 'divider' }}
                    >
                        <Refresh />
                    </IconButton>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <Button 
                        variant="outlined" 
                        color="primary" 
                        startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : <Download />}
                        onClick={handleDownload}
                        disabled={downloading}
                        sx={{ borderRadius: 2 }}
                    >
                        Download Excel
                    </Button>
                    <Button 
                        variant="contained" 
                        color="secondary" 
                        component="label"
                        startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <Upload />}
                        disabled={uploading}
                        sx={{ borderRadius: 2 }}
                    >
                        Upload Excel
                        <input 
                            type="file" 
                            hidden 
                            accept=".xlsx, .xls"
                            onChange={handleUpload} 
                        />
                    </Button>
                    <TextField
                        label="Min Wallet Amount"
                        placeholder="e.g. 3000"
                        type="number"
                        size="small"
                        value={minAmount}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (val === '' || Number(val) > 0) {
                                setMinAmount(val);
                            }
                        }}
                        sx={{ width: 220, '& .MuiOutlinedInput-root': { borderRadius: '20px', backgroundColor: 'rgba(0,0,0,0.02)' } }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            inputProps: { min: 1 }
                        }}
                    />
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 4 }}>{success}</Alert>}

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <TableContainer>
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Vendor / ID</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Contact</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Wallet Balance</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Amount Due</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : payouts.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                                        <Typography color="text.secondary">
                                            No vendors meet the minimum payout requirement of ₹{minAmount}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                payouts.map((payout, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell sx={{ fontWeight: 500 }}>
                                            {payout.vendorName || payout.shopName || payout.vendor?.name || payout.vendor?.shopName || '-'} 
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                {payout.vendorId || payout.vendor?._id || payout.id || payout._id}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{payout.mobile || payout.contact || payout.vendor?.mobile || '-'}</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>₹{payout.walletBalance || payout.balance || payout.currentBalance || 0}</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: 'success.main', fontSize: '1rem' }}>
                                            ₹{payout.payoutAmount || payout.pendingPayout || payout.walletBalance || payout.balance || payout.currentBalance || 0}
                                        </TableCell>
                                        <TableCell>
                                            <Chip label="Eligible" sx={{ backgroundColor: 'success.lighter', color: 'success.dark', fontWeight: 600 }} size="small" />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button 
                                                variant="outlined" 
                                                size="small" 
                                                sx={{ borderRadius: 2 }}
                                            >
                                                Process
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalRows}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </Box>
    );
};

export default GetAllPayoutList;
